import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Participante, Grupo } from '@/types/app';
import { grupoService } from '@/services/grupoService';
import { getAuth } from 'firebase/auth';

interface GruposContextType {
  grupos: Grupo[];
  createGrupo: (nome: string, emoji: string, participantes: Participante[], incluirCriador?: boolean) => Promise<string>;
  updateGrupo: (id: string, updates: Partial<Grupo>) => Promise<void>;
  deleteGrupo: (id: string) => Promise<void>;
  getGrupo: (id: string) => Grupo | undefined;
  addParticipanteToGrupo: (grupoId: string, participante: Participante) => Promise<void>;
  removeParticipanteFromGrupo: (grupoId: string, participanteId: string) => Promise<void>;
  updateParticipanteInAllGrupos: (participanteId: string, updates: Partial<Participante>) => Promise<void>;
  getAllParticipantes: () => Participante[];
  loading: boolean;
}

const GruposContext = createContext<GruposContextType | undefined>(undefined);

// Avatar padrão para participantes
const AVATAR_PADRAO = 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2';

export function GruposProvider({ children }: { children: React.ReactNode }) {
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadGrupos();
    }
  }, [user]);

  const loadGrupos = async () => {
    try {
      if (!user) return;
      // Usar Firestore para carregar grupos
      const result = await grupoService.getUserGrupos(user.id);
      if (result.success && result.data) {
        setGrupos(result.data);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    } finally {
      setLoading(false);
    }
  };

const saveGrupos = async (novosGrupos: Grupo[]) => {
  try {
    if (!user) return;

    for (const grupo of novosGrupos) {
      if (!grupo.id) continue;

      // Limpa e valida os dados antes de salvar
      const participantesValidados: Participante[] = grupo.participantes.map(p => ({
        id: p.id,
        nome: p.nome || '',
        email: p.email || '',
        foto: p.foto || AVATAR_PADRAO,
        chavePix: p.chavePix || undefined
      }));

      // 🔒 Filtra campos undefined e garante tipos corretos
      const grupoParaSalvar: Partial<Grupo> = {
        nome: grupo.nome || '',
        emoji: grupo.emoji || '',
        participantes: participantesValidados,
        dataCriacao: grupo.dataCriacao || new Date(),
        incluirCriador: grupo.incluirCriador === true
      };

      await grupoService.updateGrupo(grupo.id, grupoParaSalvar);
    }

    console.log('GruposContext: Grupos salvos no Firestore com sucesso.');
  } catch (error) {
    console.error('GruposContext: Erro ao salvar grupos:', error);
    throw error;
  }
};

  const createGrupo = async (nome: string, emoji: string, participantes: Participante[], incluirCriador?: boolean): Promise<string> => {
    if (!user) throw new Error('Usuário não autenticado');

    try {
      console.log('GruposContext: Criando grupo', nome);

      // --- INÍCIO DO BLOCO DE DEBUG ---
      console.log('--- DEBUG: Verificando dados antes de criar o grupo ---');

      if (user) {
        console.log('AuthContext: Usuário está logado. ID:', user.id);
      } else {
        console.error('AuthContext: ERRO, o usuário está NULO no momento de criar o grupo.');
      }

      const novoGrupoDebug = {
        nome: nome,
        participantes: participantes,
        criadorId: user ? user.id : 'ID_DO_USUARIO_NAO_ENCONTRADO'
      };

      console.log('Dados a serem enviados para o Firestore:', JSON.stringify(novoGrupoDebug, null, 2));
      console.log('--- FIM DO BLOCO DE DEBUG ---');
      // --- FIM DO BLOCO DE DEBUG ---

      // Forçar atualização do token de autenticação para garantir credencial válida
      const auth = getAuth();
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      let participantesFinais = [...participantes];

      // Se incluirCriador for true, adicionar o usuário atual
      if (incluirCriador) {
        const usuarioJaIncluido = participantesFinais.some(p => p.id === user.id);
        if (!usuarioJaIncluido) {
          participantesFinais.unshift({
            id: user.id,
            nome: user.nome,
            email: user.email,
            foto: AVATAR_PADRAO,
            chavePix: user.chavePix
          });
        }
      }

      // Garantir que todos os participantes tenham avatar padrão
      const participantesComAvatar = participantesFinais.map(p => ({
        ...p,
        foto: AVATAR_PADRAO
      }));

      const novoGrupo: Grupo = {
        id: Date.now().toString(),
        nome,
        emoji,
        participantes: participantesComAvatar,
        dataCriacao: new Date(),
        incluirCriador: incluirCriador || false
      };

      const result = await grupoService.createGrupo({
        nome: novoGrupo.nome,
        emoji: novoGrupo.emoji,
        participantes: novoGrupo.participantes,
        incluirCriador: novoGrupo.incluirCriador,
        criadorId: user.id
      });

      if (result.success && result.data?.id) {
        novoGrupo.id = result.data.id;
        const novosGrupos = [novoGrupo, ...grupos];
        setGrupos(novosGrupos);
        console.log('GruposContext: Grupo criado com sucesso no Firestore.');
        return novoGrupo.id;
      } else {
        throw new Error(result.error || 'Erro desconhecido ao criar grupo no Firestore');
      }
    } catch (error) {
      console.error('GruposContext: Erro ao criar grupo:', error);
      throw error;
    }
  };

  const updateGrupo = async (id: string, updates: Partial<Grupo>) => {
    try {
      console.log('GruposContext: Atualizando grupo', id);
      const novosGrupos = grupos.map(grupo =>
        grupo.id === id ? { ...grupo, ...updates } : grupo
      );
      setGrupos(novosGrupos);
      await saveGrupos(novosGrupos);
      console.log('GruposContext: Grupo atualizado com sucesso.');
    } catch (error) {
      console.error('GruposContext: Erro ao atualizar grupo:', error);
      throw error;
    }
  };

  const deleteGrupo = async (id: string) => {
    try {
      console.log('GruposContext: Excluindo grupo', id);
      const novosGrupos = grupos.filter(grupo => grupo.id !== id);
      setGrupos(novosGrupos);
      await grupoService.deleteGrupo(id);
      console.log('GruposContext: Grupo excluído e grupos salvos.');
    } catch (error) {
      console.error('GruposContext: Erro em deleteGrupo:', error);
      throw error;
    }
  };

  const getGrupo = (id: string): Grupo | undefined => {
    return grupos.find(grupo => grupo.id === id);
  };

  const addParticipanteToGrupo = async (grupoId: string, participante: Participante) => {
    try {
      console.log(`GruposContext: Adicionando participante ${participante.id} ao grupo ${grupoId}`);

      const grupo = grupos.find(g => g.id === grupoId);
      if (!grupo) throw new Error('Grupo não encontrado');

      // Garante que o participante tenha todos os campos necessários
      const participanteValidado: Participante = {
        id: participante.id,
        nome: participante.nome || '',
        email: participante.email || '',
        foto: participante.foto || AVATAR_PADRAO,
        chavePix: participante.chavePix || undefined
      };

      // Atualiza o estado local primeiro
      const novoGrupo = {
        ...grupo,
        participantes: [...grupo.participantes, participanteValidado]
      };

      // Atualiza o estado local
      setGrupos(grupos.map(g => g.id === grupoId ? novoGrupo : g));

      // Prepara os dados para o Firestore
      const grupoParaSalvar = {
        ...novoGrupo,
        participantes: novoGrupo.participantes.map(p => ({
          id: p.id,
          nome: p.nome || '',
          email: p.email || '',
          foto: p.foto || AVATAR_PADRAO,
          chavePix: p.chavePix || null
        }))
      };

      // Atualiza no Firestore
      await grupoService.updateGrupo(grupoId, {
        participantes: grupoParaSalvar.participantes
      });

      console.log('GruposContext: Participante adicionado com sucesso');
    } catch (error) {
      console.error('GruposContext: Erro em addParticipanteToGrupo:', error);
      // Reverte o estado local em caso de erro
      const grupoOriginal = grupos.find(g => g.id === grupoId);
      if (grupoOriginal) {
        setGrupos(grupos.map(g => g.id === grupoId ? grupoOriginal : g));
      }
      throw error;
    }
  };

  const removeParticipanteFromGrupo = async (grupoId: string, participanteId: string) => {
    try {
      console.log('GruposContext: Removendo participante', participanteId, 'do grupo', grupoId);
      const novosGrupos = grupos.map(grupo => {
        if (grupo.id === grupoId) {
          return {
            ...grupo,
            participantes: grupo.participantes.filter(p => p.id !== participanteId)
          };
        }
        return grupo;
      });
      setGrupos(novosGrupos);
      await saveGrupos(novosGrupos);
      console.log('GruposContext: Participante removido e grupos salvos.');
    } catch (error) {
      console.error('GruposContext: Erro em removeParticipanteFromGrupo:', error);
      throw error;
    }
  };

  const updateParticipanteInAllGrupos = async (participanteId: string, updates: Partial<Participante>) => {
    try {
      console.log('GruposContext: Atualizando participante', participanteId, 'em todos os grupos');
      const novosGrupos = grupos.map(grupo => ({
        ...grupo,
        participantes: grupo.participantes.map(participante =>
          participante.id === participanteId ? { ...participante, ...updates } : participante
        )
      }));
      setGrupos(novosGrupos);
      await saveGrupos(novosGrupos);
      console.log('GruposContext: Participante atualizado e grupos salvos.');
    } catch (error) {
      console.error('GruposContext: Erro em updateParticipanteInAllGrupos:', error);
      throw error;
    }
  };

  const getAllParticipantes = (): Participante[] => {
    const todosParticipantes: Participante[] = [];
    const idsUnicos = new Set<string>();

    grupos.forEach(grupo => {
      grupo.participantes.forEach(participante => {
        if (!idsUnicos.has(participante.id)) {
          idsUnicos.add(participante.id);
          todosParticipantes.push(participante);
        }
      });
    });

    return todosParticipantes;
  };

  return (
    <GruposContext.Provider value={{
      grupos,
      createGrupo,
      updateGrupo,
      deleteGrupo,
      getGrupo,
      addParticipanteToGrupo,
      removeParticipanteFromGrupo,
      updateParticipanteInAllGrupos,
      getAllParticipantes,
      loading
    }}>
      {children}
    </GruposContext.Provider>
  );
}

export function useGrupos() {
  const context = useContext(GruposContext);
  if (context === undefined) {
    throw new Error('useGrupos must be used within a GruposProvider');
  }
  return context;
}
